import React, { useState, useEffect } from "react";
import { Table, Button, Space, Tag, message, Modal, Switch, Tooltip, Select, Divider } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, FilterOutlined } from "@ant-design/icons";
import Layout from "../../components/Layout";
import { getCommissionConfigurations, deleteCommissionConfig, toggleCommissionConfigStatus, getCommissionTypes } from "../../services/commission/commissionServices";
import CommissionConfigModal from "./modals/CommissionConfigModal";
import GlassCard from "../../components/premium/GlassCard";
import moment from "moment";

const { confirm } = Modal;
const { Option } = Select;

const CommissionConfigList = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [types, setTypes] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getCommissionConfigurations();
      if (res?.success) {
        setData(res.data);
      } else {
        message.error(res?.message || "Failed to fetch configurations");
      }
    } catch (error) {
      message.error("Error fetching configurations");
    } finally {
      setLoading(false);
    }
  };

  const fetchTypes = async () => {
    try {
      const res = await getCommissionTypes();
      if (res?.success) {
        setTypes(res.data);
      }
    } catch (error) {
      console.error("Error fetching types", error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchTypes();
  }, []);

  const handleToggleStatus = async (record) => {
    try {
      const res = await toggleCommissionConfigStatus(record.id);
      if (res?.success) {
        message.success(`Status updated for ${record.commissionName}`);
        fetchData();
      } else {
        message.error(res?.message || "Failed to update status");
      }
    } catch (error) {
      message.error("Error updating status");
    }
  };

  const handleDelete = (record) => {
    confirm({
      title: "Are you sure you want to delete this configuration?",
      icon: <ExclamationCircleOutlined />,
      content: "This action will permanently remove this pricing band.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          const res = await deleteCommissionConfig(record.id);
          if (res?.success) {
            message.success("Configuration deleted successfully");
            fetchData();
          } else {
            message.error(res?.message || "Failed to delete");
          }
        } catch (error) {
          message.error("Error deleting configuration");
        }
      },
    });
  };

  const filteredData = typeFilter 
    ? data.filter(item => item.commissionTypeId === typeFilter)
    : data;

  const columns = [
    {
      title: "Commission Name",
      dataIndex: "commissionName",
      key: "commissionName",
      fixed: "left",
      width: 180,
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "Range (Min - Max)",
      key: "range",
      render: (_, record) => (
        <span>₦{record.minAmount.toLocaleString()} - ₦{record.maxAmount.toLocaleString()}</span>
      ),
    },
    {
      title: "Value",
      dataIndex: "commissionValue",
      key: "commissionValue",
      render: (val, record) => (
        <span style={{ fontWeight: 'bold', color: '#52c41a' }}>
          {record.commissionUnit === "PERCENTAGE" ? `${val}%` : `₦${val.toLocaleString()}`}
        </span>
      ),
    },
    {
      title: "Customer",
      dataIndex: "customerCategory",
      key: "customerCategory",
    },
    {
      title: "Channels",
      dataIndex: "appliesToChannel",
      key: "appliesToChannel",
      render: (channels) => (
        <Space size={[0, 4]} wrap>
          {channels?.split(',').map(c => <Tag key={c}>{c}</Tag>)}
        </Space>
      )
    },
    {
      title: "Effective Dates",
      key: "dates",
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>{moment(record.effectiveFrom).format("DD MMM YYYY")}</div>
          <div style={{ color: 'var(--text-muted)' }}>to {moment(record.effectiveTo).format("DD MMM YYYY")}</div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive, record) => (
        <Switch 
          checked={isActive} 
          onChange={() => handleToggleStatus(record)} 
          size="small"
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <Button 
              type="text" 
              icon={<EditOutlined style={{ color: "#1890ff" }} />} 
              onClick={() => {
                setSelectedRecord(record);
                setIsModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button 
              type="text" 
              icon={<DeleteOutlined style={{ color: "#ff4d4f" }} />} 
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Layout>
      <div style={{ padding: "24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "24px", alignItems: "flex-end" }}>
          <div>
            <h2 style={{ margin: 0 }}>Commission Configurations</h2>
            <p style={{ color: "var(--text-muted)" }}>Set up specific pricing bands and rules for commissions.</p>
          </div>
          <Space size="large">
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <span style={{ fontSize: "12px", color: "var(--text-muted)" }}><FilterOutlined /> Filter by Type</span>
              <Select 
                placeholder="All Types" 
                style={{ width: 220 }} 
                allowClear
                onChange={setTypeFilter}
                value={typeFilter}
              >
                {types.map(t => (
                  <Option key={t.id} value={t.id}>{t.commissionName}</Option>
                ))}
              </Select>
            </div>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={() => {
                setSelectedRecord(null);
                setIsModalVisible(true);
              }}
              style={{ borderRadius: "8px", height: "40px" }}
            >
              Add Configuration
            </Button>
          </Space>
        </div>

        <GlassCard>
          <Table 
            columns={columns} 
            dataSource={filteredData} 
            loading={loading} 
            rowKey="id"
            scroll={{ x: 1300 }}
            pagination={{ pageSize: 15 }}
          />
        </GlassCard>

        <CommissionConfigModal 
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          onSuccess={() => {
            setIsModalVisible(false);
            fetchData();
          }}
          initialValues={selectedRecord}
          commissionTypes={types}
        />
      </div>
    </Layout>
  );
};

export default CommissionConfigList;
